import { useMutation, useQuery } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import type {
  GuardianConsentAcceptance,
  GuardianConsentConfirmResponse,
} from "@bw/shared/types/generated";

export function useGuardianConsentPreview(token: string | null) {
  return useQuery({
    queryKey: ["guardian-consent-preview", token],
    queryFn: () => tournamentApi.previewGuardianConsent(token!),
    enabled: Boolean(token),
    retry: false,
  });
}

/**
 * The PUBLIC guardian-consent confirmation mutation (#1684 / #1692) — POST
 * /tournaments/entries/guardian-consent/confirm via
 * tournamentApi.confirmGuardianConsent, body { token }. Backs GuardianConsentPage,
 * which a logged-out guardian reaches from the emailed signed link.
 *
 * MONEY-touching (a PAID minor's saved card is charged OFF-SESSION server-side at
 * confirm): the hook owns NONE of the money logic — it forwards the token and lets
 * the backend charge + advance the entry. A non-2xx REJECTS so the page can branch:
 *   - 402 PAYMENT_NEEDS_ATTENTION → the off-session charge declined; offer a retry.
 *   - 400 → invalid / expired token.
 * No query invalidation: the guardian is not the entrant and holds no cached,
 * entrant-scoped queries; the page renders the returned entry directly.
 */
export function useConfirmGuardianConsent() {
  return useMutation<
    GuardianConsentConfirmResponse,
    unknown,
    { token: string; acceptances: GuardianConsentAcceptance[] }
  >({
    mutationFn: ({ token, acceptances }) =>
      tournamentApi.confirmGuardianConsent(token, acceptances),
  });
}
